<script lang="ts">
	import TopWideBlock from '$lib/components/ArticleGrid/TopWideBlock.svelte';
	import DefaultOgImage from '$lib/components/DefaultOgImage.svelte';
	import LdTag from '$lib/components/LdTag.svelte';
	import { translations } from '$lib/i18n/translations';
	import { searchSchema } from '$lib/utils/SchemaHelper';
	import type { PageData } from './$types';

	export let data: PageData;

	const property = data.property;
	const colors = property.colors;
	const t = translations(property.language);

	$: page = data.page;
	$: isSecondPage = page > 1;

	$: title = t(isSecondPage ? 'searchResultsSecondPageTitle' : 'searchResultsTitle', {
		page: page.toString()
	});

	$: description = t(
		isSecondPage ? 'searchResultsSecondPageDescription' : 'searchResultsDescription',
		{
			searchTerm: data.query,
			siteName: property.name,
			page: page.toString()
		}
	);

	$: canonical = data.canonical;
	$: query = data.query;
	$: articles = data.articles;
	$: pages = data.pages;
	$: nextPage = page < pages ? page + 1 : null;
	$: prev = data.prev;
	$: next = data.next;
</script>

<svelte:head>
	<title>{`${title} - ${property.name}`}</title>
	<link rel="canonical" href={canonical} />
	<meta name="description" content={description} />
	<meta
		name="keywords"
		content={t('searchResultsKeywords', {
			page: page.toString(),
			siteName: property.name,
			searchTerm: query
		})}
	/>

	<meta property="og:title" content={`${title} - ${property.name}`} />
	<meta property="og:description" content={description} />

	<meta property="og:url" content={canonical} />
	<meta property="og:type" content="website" />

	{#if page > 1}
		<link rel="prev" href={prev} />
	{/if}

	{#if page < pages}
		<link rel="next" href={next} />
	{/if}
</svelte:head>

<LdTag schema={searchSchema(property, articles, title, description, canonical)} />

<DefaultOgImage />

<div class="max-w-screen-lg px-4 xl:px-0 mx-auto">
	<div class="mt-5 mb-3">
		<h1 class="text-2xl font-semibold">Recherche</h1>
		<h2>Résultats de recherche pour <i>"{query}"</i></h2>
	</div>

	{#if articles.length === 0}
		<hr />
		<p class="mt-3">{t('searchResultsNoResults', { searchTerm: query })}</p>
	{:else}
		<div class="grid gap-6 grid-cols-1 md:grid-cols-2">
			{#each articles as article, i}
				<TopWideBlock {article} lazy={!(i < 4)} />
			{/each}
		</div>
	{/if}

	{#if nextPage}
		<div class="mt-5 text-right">
			<a
				href={`?q=${query}&page=${nextPage}`}
				class={`inline-flex items-center group ${colors.text} hover:${colors.hover}`}
			>
				<span class="mr-2">
					<span>{t('seemore')}</span>
					<span
						class={`block max-w-0 group-hover:max-w-full transition-all duration-500 h-0.5 ${colors.hoverBackground}`}
					></span>
				</span>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 16 16"
					fill="currentColor"
					class="w-4 h-4"
				>
					<path
						fill-rule="evenodd"
						d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z"
						clip-rule="evenodd"
					/>
				</svg>
			</a>
		</div>
	{/if}
</div>
